import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Bell, Mail, Smartphone, Loader2, Send, Lock } from 'lucide-react';
import {
  PUSH_SUPPORTED, getPushState, enablePush, disablePush, sendTestPush,
} from '../lib/pushNotifications';

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [pushState, setPushState] = useState({ supported: false, permission: 'default', subscribed: false, available: false });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get('/notifications/preferences');
      setPrefs(data);
    } catch { toast.error('Could not load preferences'); }
    setPushState(await getPushState());
  };
  useEffect(() => { refresh(); }, []);

  const savePush = async (enabled) => {
    setPrefs(p => ({ ...p, push_enabled: enabled }));
    try { await api.put('/notifications/preferences', { push_enabled: enabled }); }
    catch { toast.error('Save failed'); }
  };

  const saveCategory = async (key, enabled) => {
    setPrefs(p => ({
      ...p,
      categories: p.categories.map(c => c.key === key ? { ...c, enabled } : c),
    }));
    try { await api.put('/notifications/preferences', { categories: { [key]: enabled } }); }
    catch { toast.error('Save failed'); }
  };

  const togglePush = async () => {
    if (!PUSH_SUPPORTED) return toast.error('Push notifications aren\'t supported on this browser.');
    setBusy(true);
    if (pushState.subscribed) {
      await disablePush();
      toast.success('Push notifications disabled on this device.');
    } else {
      const r = await enablePush();
      if (r.ok) toast.success('Push notifications enabled on this device.');
      else if (r.reason === 'denied') toast.error('Permission denied. Enable it in your browser settings for jixxeoai.com.');
      else toast.error('Could not subscribe. Try again later.');
    }
    setBusy(false);
    refresh();
  };

  const test = async () => {
    const r = await sendTestPush();
    if (r.ok) toast.success('Test notification sent — check your lock screen.');
    else toast.error('Could not send test.');
  };

  if (!prefs) return <div className="p-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  const unsupportedMsg = !PUSH_SUPPORTED
    ? 'Your browser doesn\'t support push notifications. In-app notifications still work.'
    : !pushState.available
      ? 'Jixxeo AI\'s push server isn\'t ready yet. In-app notifications still work.'
      : pushState.permission === 'denied'
        ? 'You previously blocked notifications. Enable them in your browser settings for this site to receive pushes.'
        : null;

  return (
    <div className="max-w-xl mx-auto space-y-5 py-4">
      <header>
        <h1 className="font-display text-3xl font-black flex items-center gap-2">
          <Bell className="w-7 h-7 text-violet-400" /> Notifications
        </h1>
        <p className="text-sm text-slate-400">Choose what Jixxeo AI pings you about.</p>
      </header>

      {/* Device push */}
      <Card className="glass p-5" data-testid="notif-push-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/40 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-violet-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold">Push notifications on this device</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {pushState.subscribed
                    ? 'This device is registered — you\'ll get phone pings when important things happen.'
                    : 'Not registered on this device. Tap below to enable.'}
                </div>
              </div>
              <Switch
                data-testid="notif-push-device-toggle"
                checked={pushState.subscribed}
                onCheckedChange={togglePush}
                disabled={busy || !PUSH_SUPPORTED || !pushState.available}
              />
            </div>
            {unsupportedMsg && (
              <div className="text-[11px] text-amber-300 mt-2 bg-amber-500/10 border border-amber-500/20 rounded p-2">{unsupportedMsg}</div>
            )}
            {pushState.subscribed && (
              <Button data-testid="notif-send-test" onClick={test} size="sm" variant="outline" className="mt-3">
                <Send className="w-3 h-3 mr-1" /> Send test notification
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Master switches */}
      <Card className="glass p-5 space-y-4">
        <div className="flex items-center justify-between" data-testid="notif-master-push">
          <div>
            <div className="font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-violet-300" /> Push notifications</div>
            <div className="text-xs text-slate-400">Master switch for all push notifications on all your devices.</div>
          </div>
          <Switch checked={prefs.push_enabled} onCheckedChange={savePush} />
        </div>
        <div className="flex items-center justify-between opacity-60" data-testid="notif-master-email">
          <div>
            <div className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-cyan-300" /> Email notifications</div>
            <div className="text-xs text-slate-400">Coming soon — not yet active.</div>
          </div>
          <Switch checked={prefs.email_enabled} disabled />
        </div>
      </Card>

      {/* Categories */}
      <Card className="glass p-5">
        <div className="font-display font-bold mb-3">What to notify me about</div>
        <div className="space-y-1">
          {prefs.categories.map(c => (
            <div key={c.key} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0"
                 data-testid={`notif-cat-${c.key}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm">{c.label}</span>
                {c.mandatory && <Lock className="w-3 h-3 text-slate-500" title="Always on for account security" />}
              </div>
              <Switch
                data-testid={`notif-cat-toggle-${c.key}`}
                checked={c.enabled}
                disabled={c.mandatory}
                onCheckedChange={(v) => saveCategory(c.key, v)}
              />
            </div>
          ))}
        </div>
      </Card>

      <p className="text-[11px] text-slate-500 text-center">
        Jixxeo AI uses native Web Push — no third-party notification services. Your device subscription is private.
      </p>
    </div>
  );
}
